import { createAction, props } from '@ngrx/store';
import { CreateSessionDto, SessionDto, SessionReferencesDto, UpdateSessionDto } from '@shared/dto';
import { PaginatedListQuery } from '@shared/interfaces/paginated-list-query.interface';

// Read list
export const readSessions = createAction('@app/session/read', (query?: PaginatedListQuery) => ({ query }));
export const readSessionsSuccess = createAction('@app/session/read/success', props<{ data: SessionDto[]; total: number }>());
export const readSessionsFailure = createAction('@app/session/read/failure', props<{ error: any }>());

// Read single (detail) — resolves from the loaded list by id, falling back to a
// server fetch (readOne) so a reload/deep-link always has the full, fresh detail.
export const readSession = createAction('@app/session/read-one', props<{ sessionId: number }>());
export const readSessionSuccess = createAction('@app/session/read-one/success', props<{ session: SessionDto }>());
export const readSessionFailure = createAction('@app/session/read-one/failure', props<{ error: any }>());

// Create
export const createSession = createAction('@app/session/create', props<{ dto: CreateSessionDto }>());
export const createSessionSuccess = createAction('@app/session/create/success', props<{ session: SessionDto }>());
export const createSessionFailure = createAction('@app/session/create/failure', props<{ error: any }>());

// Update
export const updateSession = createAction('@app/session/update', props<{ sessionId: number; dto: UpdateSessionDto }>());
// `propagation` is present only when a strategy acted on scheduled events — it reports
// what was REALLY touched (rewritten with `propagate`, detached with `keep-scheduled`,
// recomputed at save time), so it is what the outcome message must quote, not the
// numbers previewed in the confirmation dialog.
export const updateSessionSuccess = createAction('@app/session/update/success', props<{ session: Partial<SessionDto>; propagation?: SessionReferencesDto }>());
export const updateSessionFailure = createAction('@app/session/update/failure', props<{ error: any }>());
// The edit reaches events already scheduled from this session, and the request carried
// no `strategy` — the backend refused it with 428 and we fetched the impact. Terminal
// for this round: the page shows the impact and, on a choice, re-dispatches
// `updateSession` with the SAME dto plus the chosen `strategy`.
export const updateSessionRequiresConfirmation = createAction(
  '@app/session/update/requires-confirmation',
  props<{ sessionId: number; dto: UpdateSessionDto; message: string; references: SessionReferencesDto }>(),
);

// Delete — `confirm` resends as `?confirm=true` after the 428 (active events) is accepted.
export const deleteSession = createAction('@app/session/delete', props<{ sessionId: number; confirm?: boolean }>());
export const deleteSessionSuccess = createAction('@app/session/delete/success', props<{ session: SessionDto }>());
export const deleteSessionFailure = createAction('@app/session/delete/failure', props<{ error: any }>());
// 428: the session still has ACTIVE events. Safe to delete (their snapshot is frozen) but
// deliberate — the page confirms with the backend message and resends `confirm`.
export const deleteSessionRequiresConfirmation = createAction(
  '@app/session/delete/requires-confirmation',
  props<{ sessionId: number; message: string }>(),
);

// Restore
export const restoreSession = createAction('@app/session/restore', props<{ sessionId: number }>());
export const restoreSessionSuccess = createAction('@app/session/restore/success', props<{ session: SessionDto }>());
export const restoreSessionFailure = createAction('@app/session/restore/failure', props<{ error: any }>());
